<template>
  <div
    @keydown.esc="endPolygon()"
    @keydown.delete="deletePolygon()"
    tabindex="0"
  >
    <!-- Display polygon -->
    <div
      :title="!isCreatingPolygon ? title : ''"
      class="polygon-overlay"
      :class="[
        { 'polygon-overlay--active': isSelected || isCreatingPolygon },
        { 'polygon-overlay--outline': isSelected && !isCreatingPolygon },
        {
          'polygon-overlay--outline-dashed': isTagSelected && !isCreatingPolygon
        },
        {
          'polygon-overlay--grabbable': !isCreatingPolygon
        },
        {
          'polygon-overlay--dragging': isDragging
        }
      ]"
      ref="polygonOverlay"
      @mouseup.left.stop="editPointDragEnd($event)"
      @mousemove.left.stop="editPointDrag($event)"
      @click.self="clearSelection"
    >
      <svg
        v-if="isCreatingPolygon"
        viewBox="0 0 3190 1960"
        height="100%"
        width="100%"
        fill="transparent"
        stroke="#FF7F50"
        stroke-width="1"
      >
        <polygon :points="pointToPolygon" />
      </svg>
      <svg
        v-if="!isCreatingPolygon"
        viewBox="0 0 3190 1960"
        height="100%"
        width="100%"
        :fill="getFill(editPolygon)"
        :stroke="editPolygon.stroke"
        :stroke-width="editPolygon.strokeWidth"
        stroke-linejoin="round"
        @click="onSvgClick"
      >
        <polygon
          :class="[
            { 'drop-shadow': editPolygon.dropShadow },
            { inactive: isCreatingPolygon }
          ]"
          ref="polygon"
          :points="pointToPolygon"
          @pointerdown="polygonDragStart"
          @pointermove="polygonDragMove"
          @pointerup="polygonDragEnd"
          @dblclick="onPolygonDblClick"
          @click.stop="selectPolygon"
        />
      </svg>

      <div v-if="isEditingPoints">
        <div
          v-for="(point, i) in editPolygon.points"
          :key="'point' + i"
          class="point"
          :style="`left: ${point[0] * scale}px; top: ${point[1] * scale}px;`"
          @pointerdown="editPointDragStart(i, $event)"
          @pointerup="editPointDragEnd"
          @dblclick="deletePoint(i)"
        ></div>
      </div>

      <div v-if="isAddingPoints">
        <div
          v-for="(point, i) in editPolygon.points"
          :key="'dot' + i"
          class="dot"
          :style="`left: ${point[0] * scale}px; top: ${point[1] * scale}px;`"
        ></div>
        <div
          v-for="(point, i) in intermidiatePoints"
          :key="'plus' + i"
          class="plus"
          :class="{ 'plus--hidden': !showPlusHotkey }"
          :style="`left: ${point[0] * scale}px; top: ${point[1] * scale}px;`"
          @click.left="addPointToPolygon(point, i)"
        ></div>
      </div>

      <div v-if="isSelected && polygonCornerPositions">
        <div
          v-for="(point, i) in polygonCornerPositions"
          class="transform-handle"
          :class="`transform-handle--${i}`"
          ref="transform"
          :style="`left: ${point[0] * scale}px; top: ${point[1] * scale}px;`"
          @pointerdown="polygonTransformStart(i)"
          @pointermove="e => polygonTransformMove(e, i)"
          @pointerup="polygonTransformEnd(i)"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  props: {
    id: {
      type: String,
      default: ""
    },
    polygon: {
      type: Object,
      default: () => ({
        fill: "rgba(0,0,0,0)",
        stroke: "rgba(255,255,255,0)",
        strokeWidth: 8,
        points: [],
        dropShadow: false
      })
    },
    scale: {
      type: Number,
      default: 1
    },
    isSelected: {
      type: Boolean,
      default: false
    },
    isTagSelected: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ""
    }
  },

  data() {
    return {
      isCreatingPolygon: false,
      isEditingPoints: false,
      isAddingPoints: false,

      editPolygonUpdate: 0,
      editPolygon: {},
      editPolygonPointIndex: null,
      intermidiatePoints: [],
      showPlusHotkey: false,

      activePicker: "",

      showPolygonControls: true,
      isDragging: false,
      isTransformingPolygon: false,
      polygonMovedSinceStart: false,

      dblClickDelay: 400,
      dblClicks: 0,
      dblClickTimeout: null,

      boundingMaxX: 3190,
      boundingMaxY: 1960
    };
  },

  computed: {
    ...mapState({
      activePolygonId: state => state.draggable.activePolygonId,
      editPolygonId: state => state.draggable.editPolygonId,
      deletePolygonId: state => state.draggable.deletePolygonId
    }),

    pointToPolygon() {
      if (this.editPolygon && this.editPolygon.points) {
        return this.editPolygon.points
          .map(point => `${point[0]},${point[1]}`)
          .join(" ");
      }
    },

    polygonCornerPositions() {
      if (this.editPolygon && this.editPolygon.points) {
        const xValues = this.editPolygon.points.map(point => point[0]);
        const yValues = this.editPolygon.points.map(point => point[1]);
        const xMin = Math.min(...xValues);
        const xMax = Math.max(...xValues);
        const yMin = Math.min(...yValues);
        const yMax = Math.max(...yValues);

        return [
          [xMin, yMin], // top left
          [xMax, yMin], // top right
          [xMax, yMax], // bottom right
          [xMin, yMax] // bottom left
        ];
      }

      return null;
    }
  },

  watch: {
    editPolygon: {
      handler: function(newPolygon) {
        if (newPolygon) {
          this.generateIntermidiatePoints();
          this.$emit("updatePolygon", newPolygon);
        }
      },
      deep: true
    },

    isTagSelected: {
      handler: function(newIsTagSelected) {
        if (!newIsTagSelected) {
          this.isEditingPoints = false;
          this.isAddingPoints = false;
        }
      },
      deep: true
    },

    activePolygonId(id) {
      if (id === this.id) {
        this.startPolygon();
      }
    },

    editPolygonId(id) {
      if (id === this.id) {
        // turn ON edit mode for this polygon
        this.editPolygonMode();

        // if no points exist yet, begin drawing immediately
        if (!Array.isArray(this.editPolygon.points)) {
          this.$set(this.editPolygon, "points", []);
        }

        if (!this.editPolygon.points.length) {
          this.startPolygon();
        }
      } else if (
        // if this polygon was in edit mode and the store
        // switched editPolygonId away from us (or to null),
        // exit edit mode.
        this.isEditingPoints ||
        this.isAddingPoints ||
        this.isCreatingPolygon
      ) {
        this.isEditingPoints = false;
        this.isAddingPoints = false;
        this.isCreatingPolygon = false;
        this.editPolygonPointIndex = null;
        this.isDragging = false;
        this.isTransformingPolygon = false;
      }
    },

    deletePolygonId(id) {
      if (id === this.id) {
        this.deletePolygon();
      }
    },

    isSelected(newVal) {
      if (!newVal) {
        this.isEditingPoints = false;
        this.isAddingPoints = false;
        this.isCreatingPolygon = false;
        this.editPolygonPointIndex = null;
        this.isDragging = false;
        this.isTransformingPolygon = false;
      }
    }
  },

  mounted() {
    this.editPolygon = this.polygon;
    this.generateIntermidiatePoints();

    window.addEventListener("keydown", this.onWindowKeyDown);
    window.addEventListener("keyup", this.onWindowKeyUp);
    window.addEventListener("blur", this.onWindowBlur);
  },

  beforeDestroy() {
    window.removeEventListener("keydown", this.onWindowKeyDown);
    window.removeEventListener("keyup", this.onWindowKeyUp);
    window.removeEventListener("blur", this.onWindowBlur);
  },

  methods: {
    // 🔹 Clamp a single point to canvas (similar spirit to tags)
    clampPoint(x, y) {
      const bounds = { maxX: this.boundingMaxX, maxY: this.boundingMaxY };
      if (!bounds) {
        return { x, y };
      }

      const { maxX, maxY } = bounds;

      const clampedX = Math.max(0, Math.min(x, maxX));
      const clampedY = Math.max(0, Math.min(y, maxY));

      return {
        x: Math.round(clampedX),
        y: Math.round(clampedY)
      };
    },

    generateIntermidiatePoints() {
      this.intermidiatePoints = [];
      this.editPolygon.points.forEach((point, i) => {
        if (i !== 0 && i !== this.editPolygon.points.length) {
          let prevPoint = this.editPolygon.points[i - 1];
          let x = (prevPoint[0] + point[0]) / 2;
          let y = (prevPoint[1] + point[1]) / 2;
          this.intermidiatePoints.push([x, y]);
        }

        if (i === this.editPolygon.points.length - 1) {
          let firstPoint = this.editPolygon.points[0];
          let x = (firstPoint[0] + point[0]) / 2;
          let y = (firstPoint[1] + point[1]) / 2;
          this.intermidiatePoints.push([x, y]);
        }
      });
    },

    selectPolygon() {
      if (!this.isCreatingPolygon) {
        let polygon = this.polygon;
        polygon["_id"] = this.id;

        this.$store.commit("draggable/addToSelectedElements", {
          element: this.polygon,
          elementType: "polygon",
          isSingle: true
        });
      }
    },

    onPolygonDblClick() {
      // put this polygon into edit mode via Vuex
      this.$store.commit("draggable/editPolygon", this.id);
    },

    editPolygonMode() {
      this.isAddingPoints = true;
      this.isEditingPoints = true;
      this.showPlusHotkey = false; // hidden by default
    },

    startPolygon() {
      this.isCreatingPolygon = true;
      this.editPolygon.points.push([0, 0]);
    },

    endPolygon() {
      if (this.isCreatingPolygon) {
        this.editPolygon.points.pop();
      }
      this.isCreatingPolygon = false;
    },

    addPointToPolygon(point, i) {
      this.editPolygon.points.splice(i + 1, 0, point);
      this.generateIntermidiatePoints();
    },

    deletePoint(i) {
      this.editPolygon.points.splice(i, 1);
      this.generateIntermidiatePoints();
    },

    editPointDragStart(i, e) {
      this.dblClicks++;

      if (this.dblClicks === 1) {
        // setPointerCapture seems to interfere with the dblclick event
        // this is a work around to only fire setPointerCapture on first click
        this.$refs.polygon.setPointerCapture(e.pointerId);
        this.editPolygonPointIndex = i;

        this.dblClickTimeout = setTimeout(() => {
          this.dblClicks = 0;
        }, this.dblClickDelay);
      } else {
        clearTimeout(this.dblClickTimeout);
        this.dblClicks = 0;
      }
    },

    editPointDragEnd(e) {
      if (this.isCreatingPolygon) {
        const x = e.layerX / this.scale;
        const y = e.layerY / this.scale;

        this.editPolygon.points.push([x, y]);
      } else if (this.isEditingPoints || this.isAddingPoints) {
        this.editPolygonPointIndex = null;
      } else if (!this.isTransformingPolygon) {
        this.$emit("clearSelection");
      }
    },

    editPointDrag(event) {
      if (this.isCreatingPolygon && this.editPolygon.points.length > 0) {
        let x = event.layerX / this.scale;
        let y = event.layerY / this.scale;

        const { x: cx, y: cy } = this.clampPoint(x, y);

        const lastIndex = this.editPolygon.points.length - 1;
        this.$set(this.editPolygon.points, lastIndex, [cx, cy]);
      }

      // dragging existing points
      if (
        (this.isEditingPoints || this.isAddingPoints) &&
        this.editPolygonPointIndex !== null
      ) {
        const idx = this.editPolygonPointIndex;

        // get cursor position in layer
        let x = event.layerX / this.scale;
        let y = event.layerY / this.scale;

        const { x: cx, y: cy } = this.clampPoint(x, y);

        this.$set(this.editPolygon.points, idx, [cx, cy]);
      }
    },

    deletePolygon() {
      if (window.confirm("Are you sure you want to delete this polygon?")) {
        this.editPolygon.points = [];
        this.clearSelection();
      }
    },

    getFill(polygon) {
      const fill = polygon.fill;
      const stroke = polygon.stroke;
      let fillOpacity = "";
      let strokeOpacity = "";

      if (fill) {
        fillOpacity = fill.replace(/^.*,(.+)\)/, "$1");
      }

      if (stroke) {
        strokeOpacity = stroke.replace(/^.*,(.+)\)/, "$1");
      }

      if (fillOpacity === "0" && strokeOpacity === "0") {
        return "rgba(0,0,0,.25)";
      }

      return fill;
    },

    onOverlayClick() {
      // treat clicking empty space as "click away"
      // same behaviour as pressing ESC / clicking background
      this.clearSelection();
    },

    onSvgClick() {
      // If we’re in polygon edit mode and click on empty SVG space,
      // treat it as clicking away.
      if (
        this.isEditingPoints ||
        this.isAddingPoints ||
        this.isCreatingPolygon
      ) {
        this.clearSelection();
      }
    },

    clearSelection() {
      this.isEditingPoints = false;
      this.isAddingPoints = false;
      this.isCreatingPolygon = false;
      this.showPlusHotkey = false;
      this.editPolygonPointIndex = null;
      this.isDragging = false;
      this.isTransformingPolygon = false;

      // if this polygon was in edit mode, clear it in the store
      if (this.$store.state.draggable.editPolygonId === this.id) {
        this.$store.commit("draggable/editPolygon", null);
      }

      this.$emit("clearSelection");
    },

    polygonDragStart(e) {
      this.isDragging = true;
      this.polygonMovedSinceStart = false;
      this.$refs.polygon.setPointerCapture(e.pointerId);

      this.$emit("record-before-action", {
        _id: this.id,
        polygon: JSON.parse(JSON.stringify(this.editPolygon)),
        elementType: "polygon"
      });
    },

    polygonDragMove(e) {
      // only run if actively dragging and polygon data exists
      if (!this.isDragging || !this.editPolygon || !this.editPolygon.points) {
        return;
      }

      const bounds = { maxX: this.boundingMaxX, maxY: this.boundingMaxY };
      if (!bounds) {
        // fallback to original behaviour if we somehow don't know bounds
        this.editPolygon.points.forEach(point => {
          point[0] += e.movementX / this.scale;
          point[1] += e.movementY / this.scale;
        });
        this.$set(this.editPolygon, "points", [...this.editPolygon.points]);
        return;
      }

      const { maxX, maxY } = bounds;

      const dxRequested = e.movementX / this.scale;
      const dyRequested = e.movementY / this.scale;

      if (!dxRequested && !dyRequested) return;

      // current bounding box of the polygon
      const xs = this.editPolygon.points.map(p => p[0]);
      const ys = this.editPolygon.points.map(p => p[1]);

      const xMin = Math.min(...xs);
      const xMax = Math.max(...xs);
      const yMin = Math.min(...ys);
      const yMax = Math.max(...ys);

      // allowed horizontal movement so box stays in [0, maxX]
      let dx = dxRequested;
      if (xMin + dx < 0) {
        dx = -xMin;
      }
      if (xMax + dx > maxX) {
        dx = maxX - xMax;
      }

      // allowed vertical movement so box stays in [0, maxY]
      let dy = dyRequested;
      if (yMin + dy < 0) {
        dy = -yMin;
      }
      if (yMax + dy > maxY) {
        dy = maxY - yMax;
      }

      // if both clamp to 0, polygon is already against that edge
      if (!dx && !dy) return;

      // set flag is movement happened (for undo/redo tracking)
      this.polygonMovedSinceStart = true;

      this.editPolygon.points = this.editPolygon.points.map(([px, py]) => [
        Math.round(px + dx),
        Math.round(py + dy)
      ]);

      this.$set(this.editPolygon, "points", [...this.editPolygon.points]);
    },

    polygonDragEnd() {
      this.isDragging = false;

      if (!this.polygonMovedSinceStart) return;

      this.$emit("record-action", {
        _id: this.id,
        polygon: JSON.parse(JSON.stringify(this.editPolygon)),
        elementType: "polygon"
      });
    },

    polygonTransformStart(i) {
      this.isTransformingPolygon = true;
    },

    polygonTransformMove(e, i) {
      if (this.isTransformingPolygon) {
        this.$refs.transform[i].setPointerCapture(e.pointerId);

        const h = this.polygonCornerPositions[i];

        // i = 0, top left
      }
    },

    polygonTransformEnd(i) {
      setTimeout(() => {
        this.isTransformingPolygon = false;
      }, 100);
    },

    onWindowKeyDown(e) {
      // only relevant while this polygon is in edit mode
      if (!this.isEditingPoints || !this.isAddingPoints) return;

      if (e.key === "Shift") {
        this.showPlusHotkey = true;
      }
    },

    onWindowKeyUp(e) {
      if (e.key === "Shift") {
        this.showPlusHotkey = false;
      }
    },

    onWindowBlur() {
      // if someone alt-tabs etc, don’t leave pluses stuck “on”
      this.showPlusHotkey = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.polygon-overlay {
  pointer-events: none;
  position: absolute;
  height: 100%;
  left: 0;
  top: 0;
  width: 100%;
  z-index: 5;

  &--active {
    pointer-events: auto;
    z-index: 10;
  }

  &--grabbable polygon {
    cursor: grab;
  }

  &--dragging polygon {
    cursor: grabbing;
  }

  &--outline-dashed {
    svg polygon {
      outline: 5px dashed aqua;
    }
  }

  &--outline {
    z-index: 100;
    svg polygon {
      outline: 5px solid aqua;
    }
  }

  svg polygon {
    pointer-events: auto;
  }

  .drop-shadow {
    filter: drop-shadow(10px 10px 10px black);
  }

  .inactive {
    pointer-events: none;
  }
}

.point {
  pointer-events: all;
  position: absolute;
  width: 16px;
  height: 16px;
  background: transparent;
  backdrop-filter: invert(100%);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  z-index: 20;

  &::before {
    content: "";
    height: 9px;
    width: 9px;
    height: 4px;
    width: 4px;
    // background: transparent;
    background: yellow;
    // backdrop-filter: invert(100%);
    border-radius: 50%;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  &::after {
    content: "";
  }
}
.dot {
  position: absolute;
  width: 6px;
  height: 6px;
  background: #000;
  border: 1px solid #fff;
  border-radius: 50%;
  pointer-events: none;
  transform: translate(-3px, -3px);
  z-index: 20;
}

.plus {
  align-items: center;
  background: #fff;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  justify-content: center;
  height: 14px;
  position: absolute;
  transform: translate(-7px, -7px);
  width: 14px;
  z-index: 20;
  font-size: 14px;
  color: #000;
  pointer-events: all;

  &::after {
    content: "+";
    display: flex;
    line-height: 0;
    padding: 0 0 3px 1px;
  }
}

.transform-handle {
  display: none;
  background-color: pink;
  border: 3px solid aqua;
  cursor: grab;
  position: absolute;
  height: 2em;
  width: 2em;

  &--0 {
    transform: translate(-2em, -2em);
  }

  &--1 {
    transform: translate(0, -2em);
  }

  &--3 {
    transform: translate(-2em, 0);
  }
}

.plus--hidden {
  opacity: 0;
  pointer-events: none;
}
</style>
